import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, Building2, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Persona {
  id: string;
  label: string;
  icon: React.ReactNode;
  challenges: string[];
  benefits: string[];
  result: string;
}

const personas: Persona[] = [
  {
    id: 'founder',
    label: 'Founder',
    icon: <User className="w-4 h-4" />,
    challenges: [
      'Spend 14 hours/week writing cold emails',
      '$8K/month for one average SDR',
      'Zero meetings last Tuesday'
    ],
    benefits: [
      'AI sends 1,000 emails while you code',
      'Replace a $96K/year SDR with AI',
      'Wake up to 3 booked meetings',
      'Land meetings with YC partners'
    ],
    result: 'Book 5-15 qualified meetings per week'
  },
  {
    id: 'sdr',
    label: 'SDR',
    icon: <Users className="w-4 h-4" />,
    challenges: [
      '6 hours LinkedIn stalking = 20 contacts',
      'Copy-pasting "Hope this finds you well" 200x',
      '0.5% reply rate on your best template'
    ],
    benefits: [
      'Find 500 perfect prospects in 47 seconds',
      'Each email mentions their latest funding round',
      'Only hop on calls with "Yes, interested" replies',
      'Hit 150% quota working 9-5 (not 9-9)'
    ],
    result: '3x your meeting volume'
  },
  {
    id: 'agency',
    label: 'Agency',
    icon: <Building2 className="w-4 h-4" />,
    challenges: [
      'Clients demand more leads for less',
      'Hard to scale without more headcount',
      'Proving ROI is getting harder'
    ],
    benefits: [
      'White-label solution for all clients',
      'Deliver results without hiring SDRs',
      'Scale campaigns across multiple clients',
      'Transparent reporting proves value'
    ],
    result: 'Manage 10x more campaigns per client'
  }
];

const PersonaToggle = () => {
  const [activePersona, setActivePersona] = useState('founder');
  const currentPersona = personas.find(p => p.id === activePersona) || personas[0];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
            Built for Your Role
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            See How Munger.ai Helps You
          </h2>
          <p className="text-xl text-gray-600">
            Choose your role to see specific benefits
          </p>
        </div>

        {/* Persona Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            {personas.map((persona) => (
              <button
                key={persona.id}
                onClick={() => setActivePersona(persona.id)}
                className={`
                  flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-all
                  ${activePersona === persona.id 
                    ? 'bg-blue-600 text-white shadow-lg' 
                    : 'text-gray-700 hover:text-gray-900'
                  }
                `}
              >
                {persona.icon}
                {persona.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activePersona}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Challenges */}
            <div className="bg-red-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="text-red-600">●</span> Your Current Challenges
              </h3>
              <ul className="space-y-4">
                {currentPersona.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-green-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="text-green-600">●</span> How Munger.ai Helps
              </h3>
              <ul className="space-y-4">
                {currentPersona.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Result */}
        <motion.div
          key={currentPersona.result}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-6 py-4 inline-block">
            <p className="text-blue-800 font-medium">
              Typical Results
            </p>
            <p className="text-blue-900 font-bold text-lg">
              {currentPersona.result}
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transition-all"
            onClick={() => window.open('https://calendly.com/airlius-hts/munger-ai-demo', '_blank')}
          >
            Book a Call
          </button>
        </div>
      </div>
    </section>
  );
};

export default PersonaToggle; 